/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useMemo } from 'react';

import { I18n } from '@coze-arch/i18n';

import { type LibraryResourceMetrics } from '../../hooks/use-library-resources-count';

import styles from './index.module.less';

const METRIC_PLACEHOLDER = '—';
const PERCENT_BASE = 100;
const TYPE_CLASS: Record<string, string> = {
  knowledge: styles.typeKnowledge,
  workflow: styles.typeWorkflow,
  imageflow: styles.typeImageflow,
  prompt: styles.typePrompt,
};

interface ComposeSegment {
  key: string;
  count: number;
  className: string;
  labelKey: string;
}

function buildComposeSegments(
  libraryResources: LibraryResourceMetrics,
): ComposeSegment[] {
  const items: ComposeSegment[] = [];
  if (libraryResources.knowledgeCount > 0) {
    items.push({
      key: 'knowledge',
      count: libraryResources.knowledgeCount,
      className: styles.segKnowledge,
      labelKey: 'abr_home_metric_library_knowledge',
    });
  }
  if (libraryResources.workflowCount > 0) {
    items.push({
      key: 'workflow',
      count: libraryResources.workflowCount,
      className: styles.segWorkflow,
      labelKey: 'abr_home_metric_library_workflow',
    });
  }
  if (libraryResources.imageflowCount > 0) {
    items.push({
      key: 'imageflow',
      count: libraryResources.imageflowCount,
      className: styles.segImageflow,
      labelKey: 'abr_home_metric_library_imageflow',
    });
  }
  if (libraryResources.promptCount > 0) {
    items.push({
      key: 'prompt',
      count: libraryResources.promptCount,
      className: styles.segPrompt,
      labelKey: 'abr_home_metric_library_prompt',
    });
  }
  return items;
}

export interface HeroResourceStripProps {
  libraryPath?: string;
  libraryResources: LibraryResourceMetrics;
}

export const HeroResourceStrip: FC<HeroResourceStripProps> = ({
  libraryPath,
  libraryResources,
}) => {
  const totalLabel = libraryResources.loading
    ? METRIC_PLACEHOLDER
    : String(libraryResources.total);

  const segments = useMemo(
    () => buildComposeSegments(libraryResources),
    [libraryResources],
  );

  const composeTotal = segments.reduce((sum, item) => sum + item.count, 0);

  const stripBody = (
    <div className={styles.resourceStripRow}>
      <div className={styles.resourceStripMain}>
        <div className={styles.resourceStripHead}>
          <div
            className={`${styles.stripNum} ${
              libraryResources.loading ? styles.numLoading : ''
            }`}
          >
            {totalLabel}
          </div>
          <div className={styles.stripLbl}>
            {I18n.t('abr_home_metric_library')}
          </div>
        </div>
        {composeTotal > 0 ? (
          <div className={styles.resourceComposeBar}>
            {segments.map(item => (
              <span
                key={item.key}
                className={item.className}
                style={{
                  width: `${Math.round((item.count / composeTotal) * PERCENT_BASE)}%`,
                }}
              />
            ))}
          </div>
        ) : null}
        {segments.length > 0 ? (
          <div className={styles.resourceStripTypes}>
            {segments.map((item, index) => (
              <span key={item.key}>
                {index > 0 ? ' · ' : null}
                <span className={TYPE_CLASS[item.key]}>
                  {I18n.t(item.labelKey, { count: item.count })}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <div className={styles.resourceStripTypes}>
            {I18n.t('abr_home_metric_library_empty')}
          </div>
        )}
      </div>
      <div className={styles.resourceStripLink}>
        {I18n.t('abr_home_go_library')}
      </div>
    </div>
  );

  if (libraryPath) {
    return (
      <a
        href={libraryPath}
        className={styles.heroResourceStrip}
        data-testid="abr-metric-library"
      >
        {stripBody}
      </a>
    );
  }

  return <div className={styles.heroResourceStrip}>{stripBody}</div>;
};
